import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useActiveCompetition } from "@/hooks/useActiveCompetition";
import { useSeasonSale } from "@/hooks/useSeasonSale";
import { ShoppingCart, User, Menu, ChevronDown, LogOut, LayoutDashboard, UserCircle, Instagram, Facebook, Trophy, Tag } from "lucide-react";
import TikTokIcon from "@/components/TikTokIcon";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const LOGO_URL = "/logo.webp";

const SHOP_MENU = [
  { label: "Rugby", to: "/shop/rugby" },
  { label: "Hockey", to: "/shop/hockey" },
  { label: "Netball", to: "/shop/netball" },
  { label: "Training", to: "/shop/training" },
  { label: "Specials", to: "/shop?specials=true" },
  { label: "Team Kits & Bulk Orders", to: "/bulk-orders" },
  { label: "View All", to: "/shop" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const { isLive: compLive, prizeAmount } = useActiveCompetition();
  const { isActive: saleLive } = useSeasonSale();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);

  const navLinks = [
    { label: "Home", to: "/" },
    { label: "Deals", to: "/deals" },
    { label: "Blog", to: "/blog" },
    { label: "Gallery", to: "/gallery" },
    { label: "Contact", to: "/contact" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav data-testid="main-navbar" className="sticky top-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 shrink-0">
            <img src={LOGO_URL} alt="Untamed Gear" className="h-10 sm:h-12 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" data-testid="nav-home"
              className="text-white font-bold uppercase tracking-wider text-sm hover:text-[#F7931E] transition-colors font-heading">
              Home
            </Link>
            {/* Shop dropdown */}
            <DropdownMenu open={shopOpen} onOpenChange={setShopOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-shop-dropdown"
                  onMouseEnter={() => setShopOpen(true)}
                  className="flex items-center gap-1 text-white font-bold uppercase tracking-wider text-sm hover:text-[#F7931E] transition-colors font-heading outline-none">
                  Shop <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                onMouseLeave={() => setShopOpen(false)}
                className="bg-[#171717] border-[#262626] text-white min-w-[220px]">
                {SHOP_MENU.map((s) => (
                  <DropdownMenuItem
                    key={s.label}
                    data-testid={`nav-shop-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    onClick={() => { setShopOpen(false); navigate(s.to); }}
                    className="cursor-pointer hover:bg-[#262626] focus:bg-[#262626] font-bold uppercase tracking-wide text-xs">
                    {s.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {navLinks.filter((l) => l.label !== "Home").map((l) => (
              <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
                className={`font-bold uppercase tracking-wider text-sm transition-colors font-heading ${
                  l.label === "Deals" ? "text-[#F7931E] hover:text-[#D97715]" : "text-white hover:text-[#F7931E]"
                }`}>
                {l.label}
              </Link>
            ))}
            {saleLive && (
              <Link to="/sale" data-testid="nav-sale"
                className="flex items-center gap-1.5 bg-[#F7931E] text-black font-black uppercase tracking-wider text-sm px-3 py-1.5 hover:bg-white transition-colors font-heading">
                <Tag size={15} /> Sale
              </Link>
            )}
            {compLive && (
              <Link to="/competition" data-testid="nav-competition"
                className="flex items-center gap-1.5 bg-[#F7931E] text-black font-black uppercase tracking-wider text-sm px-3 py-1.5 hover:bg-white transition-colors font-heading animate-pulse">
                <Trophy size={15} /> Win {prizeAmount}
              </Link>
            )}
          </div>

          {/* Right icons */}
          <div className="flex items-center gap-3">
            <a href="https://www.instagram.com/untamedsportsgear" target="_blank" rel="noopener noreferrer"
              data-testid="nav-instagram" className="hidden sm:block text-white hover:text-[#F7931E] transition-colors">
              <Instagram size={18} />
            </a>
            <a href="https://www.facebook.com/share/1GHfSftdJ4/" target="_blank" rel="noopener noreferrer"
              data-testid="nav-facebook" className="hidden sm:block text-white hover:text-[#F7931E] transition-colors">
              <Facebook size={18} />
            </a>
            <a href="https://www.tiktok.com/@untamed.gear" target="_blank" rel="noopener noreferrer"
              data-testid="nav-tiktok" className="hidden sm:block text-white hover:text-[#F7931E] transition-colors">
              <TikTokIcon size={18} />
            </a>
            <Link to="/cart" data-testid="cart-icon" className="relative text-white hover:text-[#F7931E] transition-colors">
              <ShoppingCart size={22} />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#F7931E] text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>

            {user && user !== false ? (
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-1 text-white hover:text-[#F7931E] transition-colors">
                  <User size={22} />
                  <ChevronDown size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#171717] border-[#262626] text-white min-w-[180px]">
                  <DropdownMenuItem data-testid="nav-profile" onClick={() => navigate("/profile")} className="cursor-pointer hover:bg-[#262626] focus:bg-[#262626]">
                    <UserCircle className="mr-2 h-4 w-4" /> Profile
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="nav-admin" onClick={() => navigate("/admin")} className="cursor-pointer hover:bg-[#262626] focus:bg-[#262626]">
                      <LayoutDashboard className="mr-2 h-4 w-4" /> Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="nav-logout" onClick={handleLogout} className="cursor-pointer hover:bg-[#262626] focus:bg-[#262626] text-red-400">
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="nav-login" className="hidden sm:flex items-center gap-2 bg-[#F7931E] text-black font-bold uppercase tracking-wide text-sm px-5 py-2 hover:bg-[#D97715] transition-colors btn-press">
                Sign In
              </Link>
            )}

            {/* Mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger data-testid="mobile-menu-trigger" className="md:hidden text-white">
                <Menu size={24} />
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0A0A0A] border-[#262626] w-[280px] overflow-y-auto">
                <div className="flex flex-col gap-4 mt-8">
                  <Link to="/" onClick={() => setOpen(false)}
                    className="text-white font-bold uppercase tracking-wider text-lg hover:text-[#F7931E] transition-colors font-heading">
                    Home
                  </Link>
                  <div>
                    <div className="text-[#A3A3A3] font-bold uppercase tracking-wider text-xs mb-2">Shop</div>
                    <div className="flex flex-col gap-2 pl-3 border-l-2 border-[#262626]">
                      {SHOP_MENU.map((s) => (
                        <Link key={s.label} to={s.to} onClick={() => setOpen(false)}
                          data-testid={`mobile-shop-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                          className="text-white font-bold uppercase tracking-wider text-base hover:text-[#F7931E] transition-colors font-heading">
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                  {navLinks.filter((l) => l.label !== "Home").map((l) => (
                    <Link key={l.label} to={l.to} onClick={() => setOpen(false)}
                      className="text-white font-bold uppercase tracking-wider text-lg hover:text-[#F7931E] transition-colors font-heading">
                      {l.label}
                    </Link>
                  ))}
                  {saleLive && (
                    <Link to="/sale" onClick={() => setOpen(false)} data-testid="mobile-nav-sale"
                      className="flex items-center justify-center gap-2 bg-[#F7931E] text-black font-black uppercase tracking-wider text-base py-3 hover:bg-white transition-colors font-heading">
                      <Tag size={18} /> Sale
                    </Link>
                  )}
                  {compLive && (
                    <Link to="/competition" onClick={() => setOpen(false)} data-testid="mobile-nav-competition"
                      className="flex items-center justify-center gap-2 bg-[#F7931E] text-black font-black uppercase tracking-wider text-base py-3 hover:bg-white transition-colors font-heading">
                      <Trophy size={18} /> Win {prizeAmount}
                    </Link>
                  )}
                  {!user || user === false ? (
                    <>
                      <Link to="/login" onClick={() => setOpen(false)}
                        className="bg-[#F7931E] text-black font-bold uppercase tracking-wide text-center py-3 hover:bg-[#D97715] transition-colors">
                        Sign In
                      </Link>
                      <Link to="/register" onClick={() => setOpen(false)}
                        className="border border-[#525252] text-white font-bold uppercase tracking-wide text-center py-3 hover:bg-[#171717] transition-colors">
                        Register
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/profile" onClick={() => setOpen(false)} className="text-white font-bold uppercase tracking-wider text-lg hover:text-[#F7931E] transition-colors font-heading">
                        Profile
                      </Link>
                      {user.role === "admin" && (
                        <Link to="/admin" onClick={() => setOpen(false)} className="text-white font-bold uppercase tracking-wider text-lg hover:text-[#F7931E] transition-colors font-heading">
                          Admin
                        </Link>
                      )}
                      <button onClick={() => { handleLogout(); setOpen(false); }} className="text-red-400 font-bold uppercase tracking-wider text-lg text-left">
                        Logout
                      </button>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
